package fr.manyverse.app.domain.util

import fr.manyverse.app.domain.model.Sound
import fr.manyverse.app.domain.model.SoundTag
import fr.manyverse.app.domain.model.SoundType

/**
 * Pure filtering/sorting logic for the sound list.
 *
 * Extracted from the presentation layer so the business rules (type/tag/search
 * filtering and the canonical sort order) live in the domain and can be unit-tested
 * without a ViewModel.
 */
object SoundFilter {

    /**
     * Filters [sounds] by the selected [types] and [tags] and a free-text [searchQuery],
     * then sorts the result by type (MUSIC, AMBIENT, EFFECT) and alphabetically by name.
     *
     * Empty [types]/[tags] mean "no filter" for that dimension; a blank [searchQuery]
     * disables name filtering.
     */
    fun filterAndSort(
        sounds: List<Sound>,
        types: Set<SoundType>,
        tags: Set<SoundTag>,
        searchQuery: String
    ): List<Sound> {
        val typeFiltered = if (types.isEmpty()) sounds else sounds.filter { it.type in types }

        val tagFiltered = if (tags.isEmpty()) {
            typeFiltered
        } else {
            typeFiltered.filter { sound -> tags.any { tag -> sound.tags.contains(tag) } }
        }

        val searchFiltered = if (searchQuery.isBlank()) {
            tagFiltered
        } else {
            val normalizedQuery = searchQuery.normalizeForSearch()
            tagFiltered.filter { sound ->
                sound.name.normalizeForSearch().contains(normalizedQuery)
            }
        }

        return searchFiltered.sortedWith(
            compareBy<Sound> { sound ->
                when (sound.type) {
                    SoundType.MUSIC -> 0
                    SoundType.AMBIENT -> 1
                    SoundType.EFFECT -> 2
                }
            }.thenBy { it.name.lowercase() }
        )
    }
}
